import { AxIconWrapper, AxTable } from "@dynata/axiom3";
import { IGetAllProjectsResponseModel } from "@lib/types";
import { ReactNode } from "react";
import { useToggleFavoriteProject, useUpdateProject } from "hooks/projects";
import { UseMutateAsyncFunction } from "@tanstack/react-query";

import "features/ad-hoc-projects/projects-table.scss";
import { Link } from "react-router-dom";
import paths from "paths";
import { EditableDescription } from "./editable-description";

// https://ant.design/components/table#column
interface ITableColumn {
  title: string;
  dataIndex: string;
  key: string;
  className?: string;
  render?: (text: string, record: IProjectsTableData) => ReactNode;
}

export interface IProjectsTableData extends IGetAllProjectsResponseModel {
  isFavorite: boolean;
}

function getColumns({
  toggleFavorite,
  accountNumber,
}: {
  toggleFavorite: UseMutateAsyncFunction<
    void,
    unknown,
    { projectId: number; isFavorite: boolean },
    unknown
  >;
  accountNumber: number;
}) {
  return new Array<ITableColumn>(
    {
      render: (_, record) => (
        <AxIconWrapper
          onClick={async () => {
            await toggleFavorite({
              projectId: record.id,
              isFavorite: record.isFavorite,
            });
          }}
          type={record.isFavorite ? "solid" : "light"}
          icon="fa-star"
          size="20"
        />
      ),
      dataIndex: "isFavorite",
      title: "Favorite",
      key: "favorite",
    },
    {
      title: "Project Id",
      dataIndex: "id",
      key: "id",
      render: (_, record) => (
        <Link to={`${record.id}/${paths.authenticated.AUDIENCES}`}>
          {record.id}
        </Link>
      ),
    },
    {
      title: "Order Number",
      dataIndex: "order_number",
      key: "order_number",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (_, record) => {
        const { mutateAsync: updateProject } = useUpdateProject(
          accountNumber,
          record.id,
        );

        const submitUpdate = async (value: string) => {
          return await updateProject({ description: value });
        };

        return (
          <EditableDescription
            initialValue={record.description}
            onSubmit={submitUpdate}
          >
            {record.description}
          </EditableDescription>
        );
      },
    },
    {
      title: "Audiences",
      dataIndex: "audiences",
      key: "audiences",
    },
  );
}

export interface IProjectsTableProps {
  dataSource: Array<IProjectsTableData>;
  loading: boolean;
  userId: number;
  accountNumber: number;
}

export function ProjectsTable({
  userId,
  accountNumber,
  loading,
  ...rest
}: IProjectsTableProps) {
  const { mutateAsync: toggleFavorite, isPending: toggleIsPending } =
    useToggleFavoriteProject(userId, accountNumber);

  const columns = getColumns({ toggleFavorite, accountNumber });
  const tableProps = {
    ...rest,
    columns,
    pagination: false,
    loading: loading || toggleIsPending,
  };

  return <AxTable {...tableProps} />;
}
